import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
// store
import { useSelector, useDispatch, SET_RANGES_SENSOR } from 'store';
// sections
import SensorsChart from './ContentChart';
// components
import { Label, TextFields, Message as FormMessage } from '../../../components/hook-form';
// @devextreme
import ArrayStore from 'devextreme/data/array_store';
import Chart, { CommonSeriesSettings, Series, Legend, Tooltip, ConstantLine, ValueAxis, Annotation, CommonAxisSettings, VisualRange } from 'devextreme-react/chart';
import { Popover } from 'devextreme-react/popover';
// @moment
import moment from 'moment-timezone';
// utils
import { timezones } from '../../../utils/format';
// assets
import { ReactComponent as Line } from '../../../assets/real-time/line.svg';
import { ReactComponent as Recently } from '../../../assets/real-time/recently.svg';
import { ReactComponent as Settings } from 'assets/real-time/settings.svg';
// styles
import { LinePopover, RealTimeContent, RealTimeChart, RangesPopover, TextSwitch, PrimaryButton } from '../../../styles';
// apis
import { getRecently, getAiRecently, getTemperature } from '../../../apis';

// ----------------------------------------------------------------------

interface Props {
  info: React.MutableRefObject<any>;
  setRecently: React.Dispatch<any>;
  alarmLines: {
    [key: string]: any;
  };
  setAlarmLines: React.Dispatch<React.SetStateAction<{
    [key: string]: any;
  }>>;
  aiAlarmLines: {
    [key: string]: any;
  };
  setAiAlarmLines: React.Dispatch<React.SetStateAction<{
    [key: string]: any;
  }>>;
  data: any[];
  group: any;
  visibleRanges: {
    [key: string]: any;
  };
  setVisibleRanges: React.Dispatch<React.SetStateAction<{
    [key: string]: any;
  }>>;
  visibleLines: any;
  setVisibleLines: React.Dispatch<any>;
  lines: any;
  setLines: React.Dispatch<any>;
  ranges: any;
  setRanges: React.Dispatch<any>;
  aiVisibleLines: any;
  setAiVisibleLines: React.Dispatch<any>;
  aiLines: any;
  setAiLines: React.Dispatch<any>;
  aiRanges: any;
  setAiRanges: React.Dispatch<any>;
};

// ----------------------------------------------------------------------

export default function ContentSensors({
  info,
  setRecently,
  alarmLines,
  setAlarmLines,
  aiAlarmLines,
  setAiAlarmLines,
  data,
  group,
  visibleRanges,
  setVisibleRanges,
  visibleLines,
  setVisibleLines,
  lines,
  setLines,
  ranges,
  setRanges,
  aiVisibleLines,
  setAiVisibleLines,
  aiLines,
  setAiLines,
  aiRanges,
  setAiRanges
}: Props) {
  const dispatch = useDispatch();

  const axisRanges = useSelector((state) => state.rangesSensor);
  const [ axisRange, setAxisRange ] = useState(axisRanges);
  const [ message, setMessage ] = useState(false);

  useEffect(() => {
    setAxisRange(axisRanges);
  }, [axisRanges]);

  const handleRanges = (name: string) => {
    if ( axisRange[name].manual && ( axisRange[name].startValue > axisRange[name].endValue )) {
      return setMessage(true);
    } else {
      dispatch( SET_RANGES_SENSOR({ ...axisRanges, [name]: axisRange[name] }));
      setVisibleRanges({ ...visibleRanges, [name]: false });
      setMessage(false);
    }
  };

  const groupId = group.id;

  const [ datasets, setDatasets ] = useState(null);
  const [ datasource, setDatasource ] = useState<any>([]);

  const [ aiDatasets, setAiDatasets ] = useState(null);
  const [ aiDatasource, setAiDatasource ] = useState<any>([]);

  useEffect(() => {
    const infoCurrent = info.current;
    const dataset: {[key: string]: any} = {};
    const aiDataset: {[key: string]: any} = {};

    data.forEach((element: any) => {
      if ( infoCurrent.iotDeviceId.includes(element.iotDeviceId) ) {
        dataset[infoCurrent[element.iotDeviceId].name] = {
          ...dataset[infoCurrent[element.iotDeviceId].name],
          [element.iotDeviceId]: {
            id: element.iotDeviceId,
            measuredValue: element.waterTemperature,
            acquisitionAt: element.acquisitionAt,
            location: infoCurrent[element.iotDeviceId].location
          }
        };
        
        element.waterQualities.forEach((item: any) => {
          if ( item.waterQualitySensorId ) {
            dataset[infoCurrent[item.waterQualitySensorId].name] = {
              ...dataset[infoCurrent[item.waterQualitySensorId].name],
              [item.waterQualitySensorId]: {
                id: item.waterQualitySensorId,
                measuredValue: item.measuredValue,
                alarm: item.alarm,
                acquisitionAt: element.acquisitionAt,
                location: infoCurrent[element.iotDeviceId].location
              }
            };
          }
        });
        
        element.waterQualityPredictions.forEach((item: any) => {
          if ( item.waterQualityPredictionId ) {
            aiDataset[infoCurrent[item.waterQualityPredictionId].name] = {
              ...aiDataset[infoCurrent[item.waterQualityPredictionId].name],
              [item.waterQualityPredictionId]: {
                id: item.waterQualityPredictionId,
                measuredValue: item.predictionValue ? item.predictionValue : null,
                alarm: item.alarm,
                acquisitionAt: element.acquisitionAt,
                location: infoCurrent[element.iotDeviceId].location
              }
            };
          }
        });
      }
    });
    setDatasets(dataset);
    setAiDatasets(aiDataset);

    if ( dataset && Object.keys(dataset).length > 0 ) {
      const dataSort: any = [];
      info.current.sensors.forEach((element: any) => {
        dataSort.push( new ArrayStore({key: element, data: Object.values(dataset[element])}) );
      });
      dataSort.push( new ArrayStore({key: 'waterTemperature', data: Object.values(dataset.waterTemperature)}) );

      setDatasource(dataSort);
    } else {
      setDatasource([]);
    }
    if ( aiDataset && Object.keys(aiDataset).length > 0 ) {
      const dataSort: any = [];
      info.current.predictions.forEach((element: any) => {
        dataSort.push( new ArrayStore({key: element, data: Object.values(aiDataset[element])}) );
      });

      setAiDatasource(dataSort);
    } else {
      setAiDatasource([]);
    }

    const alarmLineSettings: {[key: string]: any} = {};
    infoCurrent.sensors.forEach((item: any) => {
      alarmLineSettings[item] = [];
      if ( Object.keys(dataset).length > 0 ) {
        Object.keys(dataset[item]).forEach((id: any) => {
          if ( infoCurrent[id].alarmType !== 'OFF' ) {
            alarmLineSettings[item].push(id);
          }
        });
      }
    });

    const aiAlarmLineSettings: {[key: string]: any} = {};
    infoCurrent.predictions.forEach((item: any) => {
      aiAlarmLineSettings[item] = [];
      if ( Object.keys(aiDataset).length > 0 && Object.keys(aiDataset[item]).length > 0 ) {
        Object.keys(aiDataset[item]).forEach((id: any) => {
          if ( infoCurrent[id].alarmType !== 'OFF' ) aiAlarmLineSettings[item].push(id);
        });
      }
    });

    setAiAlarmLines(aiAlarmLineSettings);
    setAlarmLines(alarmLineSettings);
  }, [data, info.current]);

  
  function deepCopy(object: any) { 
    if (object === null || typeof object !== "object") {    return object;  } 
    const copy: any = Array.isArray(object) ? [] : {};
    for (let key of Object.keys(object)) {copy[key] = deepCopy(object[key] ); 

    }
    return copy;
  }

  useEffect(() => {
    if ( datasets && aiDatasets ) {
      const datasetCurrent = deepCopy(datasets);
      const aiDatasetCurrent = { ...aiDatasets };
      

      if ( datasetCurrent && Object.keys(datasetCurrent).length > 0 ) {
        const dataSort: any = [];
        info.current.sensors.forEach((element: any) => {
          dataSort.push( new ArrayStore({key: element, data: Object.values(datasetCurrent[element])}) );
        });
        dataSort.push( new ArrayStore({key: 'waterTemperature', data: Object.values(datasetCurrent.waterTemperature)}) );

        setDatasource(dataSort);
      } else {
        setDatasource([]);
      }
      
      if ( aiDatasetCurrent && Object.keys(aiDatasetCurrent).length > 0 ) {
        const dataSort: any = [];
        info.current.predictions.forEach((element: any) => {
          dataSort.push( new ArrayStore({key: element, data: Object.values(aiDatasetCurrent[element])}) );
        });

        setAiDatasource(dataSort);
      } else {
        setAiDatasource([]);
      }
    }
  }, [ datasets, aiDatasets ]);

  useEffect(() => {
    if ( lines ) {
      const rangeObj: any = {};
      Object.keys(lines).forEach((line) => {
        if ( lines[line] ) return rangeObj[line] = {start: info.current[lines[line]].alarmLowerLimit, end: info.current[lines[line]].alarmUpperLimit};
        rangeObj[line] = { start: undefined, end: undefined };
      });
      setRanges(rangeObj);
    }
  }, [lines]);

  useEffect(() => {
    if ( aiLines ) {
      const rangeObj: any = {};
      Object.keys(aiLines).forEach((line) => {
        if ( aiLines[line] ) return rangeObj[line] = {start: info.current[aiLines[line]].alarmLowerLimit, end: info.current[aiLines[line]].alarmUpperLimit};
        rangeObj[line] = { start: undefined, end: undefined };
      });
      setAiRanges(rangeObj);
    }
  }, [aiLines]);
  

  const handleRecently = useCallback((name: string) => {
    const start = timezones(moment().utc().subtract(479, 'minutes').format(), 'YYYY-MM-DDTHH:mm:ss');
    const end = timezones(moment().utc().format(), 'YYYY-MM-DDTHH:mm:ss');

    if ( name !== 'waterTemperature' ) {
      getRecently(groupId, name, start, end)
        .then((response) => {
          if ( response.length > 0 ) {
            let id = response.map((element: any) => element.waterQualitySensorId);
            const dataset: {[key: string]: any} = {};
            const dataArr: any = [];
            response.forEach((element: any) => {
              const sets: any = [];
              if ( element.waterQualities.length > 0 ) {
                sets.push(
                  ...element.waterQualities.map((item: any) => { return {
                    [element.waterQualitySensorId]: item.measuredValue,
                    acquisitionAt: timezones(item.acquisitionAt, 'YYYY-MM-DD HH:mm:ss')
                  }})
                );
                dataArr.push(
                  ...element.waterQualities.map((item: any) => { return item.measuredValue; })
                );
              }
              dataset[element.waterQualitySensorId] = sets;
            });
            
            let sort = id.map((element: any) => {
              return {id: element, num: info.current[element].number}
            });
            sort = sort.sort((a: any, b: any) => a.num - b.num);
            const sortId = sort.map((element: any) => element.id);
            id = sortId;

            setRecently({name, id, unit: info.current[id[0]].unit, dataset, dataArr});
          } else {
            setRecently({name, id: [], unit: '', dataset: [], dataArr: []});
          }
        })
        .catch(() => setRecently({name, id: [], dataset: [], dataArr: []}))
    } else if ( name === 'waterTemperature' ) {
      getTemperature(groupId, start, end)
        .then((response) => {
          if ( response.length > 0 ) {
            let id: string[] = [];
            response.forEach((element: any) => { if (info.current.iotDeviceId.includes(element.iotDeviceId)) return id.push(element.iotDeviceId); });
            const dataset: {[key: string]: any} = {};
            const dataArr: any = [];
            response.forEach((element: any) => {
              const sets: any = [];
              if ( element.waterTemperatures.length > 0 ) {
                sets.push(
                  ...element.waterTemperatures.map((item: any) => { return {
                    [element.iotDeviceId]: item.waterTemperature,
                    acquisitionAt: timezones(item.acquisitionAt, 'YYYY-MM-DD HH:mm:ss')
                  }})
                );
                dataArr.push(
                  ...element.waterTemperatures.map((item: any) => { return item.waterTemperature; })
                );
              }
              dataset[element.iotDeviceId] = sets;
            });
            
            let sort = id.map((element: any) => {
              return {id: element, num: info.current[element].number}
            });
            sort = sort.sort((a: any, b: any) => a.num - b.num);
            const sortId = sort.map((element: any) => element.id);
            id = sortId;
            
            setRecently({name, id, unit: '℃', dataset, dataArr: dataArr});
          } else {
            setRecently({name, id: [], unit: '', dataset: [], dataArr: []});
          }
        })
        .catch(() => setRecently({name, id: [], dataset: [], dataArr: []}))
    }
  }, []);

  const handleAiRecently = (name: string) => {
    const start = timezones(moment().utc().subtract(479, 'minutes').format(), 'YYYY-MM-DDTHH:mm:ss');
    const end = timezones(moment().utc().format(), 'YYYY-MM-DDTHH:mm:ss');
    getAiRecently(groupId, name, start, end)
      .then((response) => {
        if ( response.length > 0 ) {
          let id = response.map((element: any) => element.waterQualityPredictionId);
          const dataset: {[key: string]: any} = {};
          const dataArr: any = [];
          response.forEach((element: any) => {
            const sets: any = [];
            if ( element.waterQualityPredictions.length > 0 ) {
              sets.push(
                ...element.waterQualityPredictions.map((item: any) => { return {
                  [element.waterQualityPredictionId]: item.predictionValue,
                  acquisitionAt: timezones(item.acquisitionAt, 'YYYY-MM-DD HH:mm:ss')
                }})
              );
              dataArr.push(
                ...element.waterQualityPredictions.map((item: any) => { return item.predictionValue; })
              );
            }
            dataset[element.waterQualityPredictionId] = sets;
          });

          let sort = id.map((element: any) => {
            return {id: element, num: info.current[element].number}
          });
          sort = sort.sort((a: any, b: any) => a.num - b.num);
          const sortId = sort.map((element: any) => element.id);
          id = sortId;

          setRecently({name, id, unit: info.current[id[0]].unit, dataset, dataArr});
        } else {
          setRecently({name, id: [], unit: '', dataset: [], dataArr: []});
        }
      })
      .catch(() => setRecently({name, id: [], dataset: [], dataArr: []}))
  };

  return (
    <>
      <p style={{
        'fontSize': '17px',
        'color': '#3E4F70',
        'marginBottom': '10px',
      }}>
        <span style={{'fontWeight': 'bold'}}>·</span> 센서 측정 값
      </p>
      <RealTimeContent>
        { datasource.map((source: any, index: number) => (
          <RealTimeChart key={index}>
            <div>
              <p>{ source._key === 'waterTemperature' ? group.waterTemperature : source._key }</p>

              <div>
                { source._key !== 'waterTemperature' &&
                  <button id={source._key} onClick={() => setVisibleLines({...visibleLines, [source._key]: true})}><Line /></button>
                }
                <button onClick={() => handleRecently(source._key)}><Recently /></button>
                <button id={source._key} onClick={() => setVisibleRanges({ ...visibleRanges, [source._key]: !visibleRanges[source._key] })}><Settings /></button>
              </div>
            </div>
            <Popover
              target={`#${source._key}`}
              visible={visibleLines[source._key]}
              hideOnOutsideClick={true}
              onHiding={() => setVisibleLines({...visibleLines, [source._key]: false})}
            >
              <LinePopover>
                <p>알람 기준선 설정</p>
                { source._key !== 'waterTemperature' &&
                  alarmLines[source._key].length > 0 ?
                  alarmLines[source._key].map((id: any) => (
                      <label key={id}>
                        <input type='checkbox' id={id} checked={lines[source._key] === id} onChange={(event) => setLines({...lines, [source._key]: event.target.checked ? id : false})} />
                        <label htmlFor={id} />
                        { info.current[id]?.location }
                      </label>
                    )
                  ) : (
                    <label>설정 없음</label>
                  )
                }
              </LinePopover>
            </Popover>
            <Popover
              target={`#${source._key}`}
              visible={visibleRanges[source._key]}
              hideOnOutsideClick={true}
              onHiding={() => {
                setVisibleRanges({ ...visibleRanges, [source._key]: false });
                setAxisRange(axisRanges);
                setMessage(false);
              }}
            >
              <RangesPopover>
                <p>데이터 범위 설정</p>
                <div>
                  { axisRange &&
                  <>
                    <div>
                      <div>
                        <TextSwitch $text={ axisRange[source._key]?.manual ? '수동' : '자동' }>
                          <label>
                            <input
                              type='checkbox'
                              checked={axisRange[source._key]?.manual}
                              onChange={(event) => setAxisRange({
                                ...axisRange,
                                [source._key]: {
                                  ...axisRange[source._key],
                                  manual: event.target.checked
                                }
                              })}
                            />
                          </label>
                        </TextSwitch>
                        <TextFields
                          type='number'
                          disabled={ axisRange[source._key]?.manual ? false : true }
                          value={ axisRange[source._key].startValue }
                          onChange={(event) => setAxisRange({
                            ...axisRange,
                            [source._key]: {
                              ...axisRange[source._key],
                              startValue: Number(event.target.value)
                            }
                          })}
                        />
                        <p>~</p>
                        <TextFields
                          type='number'
                          disabled={ axisRange[source._key]?.manual ? false : true }
                          value={ axisRange[source._key].endValue }
                          onChange={(event) => setAxisRange({
                            ...axisRange,
                            [source._key]: {
                              ...axisRange[source._key],
                              endValue: Number(event.target.value)
                            }
                          })}
                        />
                      </div>
                    </div>
                    { message &&
                      <FormMessage>입력하신 범위를 다시 확인해 주세요.</FormMessage>
                    }
                    <PrimaryButton type='button' onClick={() => handleRanges(source._key)}>저장</PrimaryButton>
                  </>
                  }
                </div>
              </RangesPopover>
            </Popover>
            
            <SensorsChart source={source} axisRanges={axisRanges} info={info} aiRanges={ranges} aiLines={lines} />
          </RealTimeChart>
        ))}
      </RealTimeContent>

      <p style={{
        'fontSize': '17px',
        'color': '#3E4F70',
        'margin': '30px 0 10px',
      }}>
        <span style={{'fontWeight': 'bold'}}>·</span> AI 예측 값
      </p>
      <RealTimeContent>
        { aiDatasource.map((source: any, index: number) => (
          <RealTimeChart key={index}>
            <div>
              <p>{ source._key }</p>

              <div>
                <button id={source._key} onClick={() => setAiVisibleLines({...aiVisibleLines, [source._key]: true})}><Line /></button>
                <button onClick={() => handleAiRecently(source._key)}><Recently /></button>
                <button id={source._key} onClick={() => setVisibleRanges({ ...visibleRanges, [source._key]: !visibleRanges[source._key] })}><Settings /></button>
              </div>
            </div>
            <Popover
              target={`#${source._key}`}
              visible={aiVisibleLines[source._key]}
              hideOnOutsideClick={true}
              onHiding={() => setAiVisibleLines({...aiVisibleLines, [source._key]: false})}
            >
              <LinePopover>
                <p>알람 기준선 설정</p>
                { aiAlarmLines[source._key].length > 0 ?
                  aiAlarmLines[source._key].map((id: any) => (
                      <label key={id}>
                        <input type='checkbox' id={id} checked={aiLines[source._key] === id} onChange={(event) => setAiLines({...aiLines, [source._key]: event.target.checked ? id : false})} />
                        <label htmlFor={id} />
                        { info.current[id]?.location }
                      </label>
                    )
                  ) : (
                    <label>설정 없음</label>
                  )
                }
              </LinePopover>
            </Popover>
            <Popover
              target={`#${source._key}`}
              visible={visibleRanges[source._key]}
              hideOnOutsideClick={true}
              onHiding={() => {
                setVisibleRanges({ ...visibleRanges, [source._key]: false });
                setAxisRange(axisRanges);
                setMessage(false);
              }}
            >
              <RangesPopover>
                <p>데이터 범위 설정</p>
                <div>
                  { axisRange &&
                  <>
                    <div>
                      <div>
                        <TextSwitch $text={ axisRange[source._key]?.manual ? '수동' : '자동' }>
                          <label>
                            <input
                              type='checkbox'
                              checked={axisRange[source._key]?.manual}
                              onChange={(event) => setAxisRange({
                                ...axisRange,
                                [source._key]: {
                                  ...axisRange[source._key],
                                  manual: event.target.checked
                                }
                              })}
                            />
                          </label>
                        </TextSwitch>
                        <TextFields
                          type='number'
                          disabled={ axisRange[source._key]?.manual ? false : true }
                          value={ axisRange[source._key].startValue }
                          onChange={(event) => setAxisRange({
                            ...axisRange,
                            [source._key]: {
                              ...axisRange[source._key],
                              startValue: Number(event.target.value)
                            }
                          })}
                        />
                        <p>~</p>
                        <TextFields
                          type='number'
                          disabled={ axisRange[source._key]?.manual ? false : true }
                          value={ axisRange[source._key].endValue }
                          onChange={(event) => setAxisRange({
                            ...axisRange,
                            [source._key]: {
                              ...axisRange[source._key],
                              endValue: Number(event.target.value)
                            }
                          })}
                        />
                      </div>
                    </div>
                    { message &&
                      <FormMessage>입력하신 범위를 다시 확인해 주세요.</FormMessage>
                    }
                    <PrimaryButton type='button' onClick={() => handleRanges(source._key)}>저장</PrimaryButton>
                  </>
                  }
                </div>
              </RangesPopover>
            </Popover>

            <SensorsChart source={source} axisRanges={axisRanges} info={info} aiRanges={aiRanges} aiLines={aiLines} />
          </RealTimeChart>
        ))}
      </RealTimeContent>
    </>
  );
}